import { useEffect, useRef, useState } from 'react';
import { LeafPane } from './LeafPane';
import type { DragState, DropTarget, Layout, OpenTabOptions } from './types';

interface RendererProps {
  node: Layout;
  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
  activeLeafId: string | null;
  setActiveLeafId: (id: string) => void;
  onClose: (leafId: string, tabId: string) => void;
  onPin: (leafId: string, tabId: string) => void;
  onActivate: (leafId: string, tabId: string) => void;
  onDragEnd: (sourceLeafId: string, tabId: string, target: DropTarget) => void;
  dragState: DragState | null;
  setDragState: (s: DragState | null) => void;
  openTabInLeaf: (
    leafId: string,
    viewId: string,
    payload?: Record<string, unknown>,
    label?: string,
    opts?: OpenTabOptions,
  ) => void;
}

export function TreeRenderer(props: RendererProps) {
  const { node } = props;

  if (node.type === 'leaf') {
    return (
      <LeafPane
        leaf={node}
        activeLeafId={props.activeLeafId}
        setActiveLeafId={props.setActiveLeafId}
        onClose={props.onClose}
        onPin={props.onPin}
        onActivate={props.onActivate}
        onDragEnd={props.onDragEnd}
        dragState={props.dragState}
        setDragState={props.setDragState}
        openTabInLeaf={props.openTabInLeaf}
      />
    );
  }

  return <SplitRenderer {...props} />;
}

function SplitRenderer(props: RendererProps) {
  const { node, setLayout } = props;
  if (node.type !== 'split') return null;

  const [splitSize, setSplitSize] = useState(node.size || 0.5);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);

  useEffect(() => setSplitSize(node.size || 0.5), [node.size]);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
  };

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      let pct = node.dir === 'v' ? (e.clientX - r.left) / r.width : (e.clientY - r.top) / r.height;
      pct = Math.max(0.15, Math.min(0.85, pct));
      setSplitSize(pct);
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setLayout((prev) => updateSplitSize(prev, node, splitSize));
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [node, splitSize, setLayout]);

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: node.dir === 'v' ? 'row' : 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div
        style={{
          flexBasis: `${splitSize * 100}%`,
          flexGrow: 0,
          flexShrink: 0,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
        }}
      >
        <TreeRenderer {...props} node={node.a} />
      </div>
      <div
        onMouseDown={onMouseDown}
        style={{
          width: node.dir === 'v' ? 4 : 'auto',
          height: node.dir === 'h' ? 4 : 'auto',
          background: 'var(--border)',
          cursor: node.dir === 'v' ? 'col-resize' : 'row-resize',
          flexShrink: 0,
          userSelect: 'none',
        }}
      />
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
        <TreeRenderer {...props} node={node.b} />
      </div>
    </div>
  );
}

function updateSplitSize(tree: Layout, targetNode: Layout, size: number): Layout {
  if (tree === targetNode) {
    return tree.type === 'split' ? { ...tree, size } : tree;
  }
  if (tree.type === 'split') {
    return { ...tree, a: updateSplitSize(tree.a, targetNode, size), b: updateSplitSize(tree.b, targetNode, size) };
  }
  return tree;
}
